import React, { useEffect, useMemo, useState } from "react";
import {
  downloadInvoicePdf,
  emailInvoice,
  fetchInvoiceById,
  fetchInvoices,
  InvoiceDetail,
  InvoiceSummary,
} from "../../../services/billingService";
import {
  screenHeaderStyle,
  reviewScreenButtonWithColor,
  reviewScreenButtonWithOutColor,
  useSharedValue,
} from "../../Constants/constants";
import { getReport } from "../../../API_Endpoints/APIEndpoints";
import { jsPDF } from "jspdf";
import logo from "../../../images/Extractor logo.png";

export default function BillingPage() {
  const { defaultOrganization, defaultUser } = useSharedValue();
  const [loading, setLoading] = useState(false);
  const [invoices, setInvoices] = useState<InvoiceSummary[]>([]);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState<string | null>(null);
  const [selectedInvoice, setSelectedInvoice] = useState<InvoiceDetail | null>(null);
  const [emailTo, setEmailTo] = useState<string>("");
  const [message, setMessage] = useState<string>("");
  // Use global organization/user selection from app header
  const [usage, setUsage] = useState<{ totalPages: number; amount: number } | null>(null);

  const organizationId = useMemo(() => {
    return (
      defaultOrganization?.id || JSON.parse(localStorage.getItem("token") || "{}")?.organizationId
    );
  }, [defaultOrganization]);

  useEffect(() => {
    const load = async () => {
      if (!organizationId) return;
      setLoading(true);
      try {
        const data = await fetchInvoices(String(organizationId));
        setInvoices(data);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [organizationId]);

  const computeUsageFromReports = async () => {
    setLoading(true);
    setUsage(null);
    try {
      const result: any = await getReport(defaultUser, defaultOrganization);
      if (result && result.success) {
        const rows = (result.data?.data || result.data || []).map((item: any) => {
          try {
            return JSON.parse(item.json);
          } catch {
            return {};
          }
        });
        const totalPages = rows.reduce((sum: number, r: any) => {
          const val = r["Total Pages"] || r["totalPages"] || r.totalPage || r.totalPages || 0;
          const num = typeof val === "string" ? parseInt(val, 10) : Number(val) || 0;
          return sum + (isNaN(num) ? 0 : num);
        }, 0);
        const amount = Math.round(totalPages * 0.1 * 100) / 100; // £0.10 per page
        setUsage({ totalPages, amount });
      }
    } finally {
      setLoading(false);
    }
  };

  // Auto-compute whenever global org/user changes
  useEffect(() => {
    computeUsageFromReports();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultOrganization, defaultUser]);

  const downloadUsageInvoicePdf = () => {
    if (!usage) return;
    const doc = new jsPDF({ unit: "pt" });
    try {
      doc.addImage(logo as any, "PNG", 40, 32, 120, 40);
    } catch {}
    doc.setFontSize(18);
    doc.text("Invoice", 40, 100);
    doc.setFontSize(12);
    const orgName =
      defaultOrganization?.name ||
      JSON.parse(localStorage.getItem("token") || "{}")?.organizationName ||
      "Organization";
    const today = new Date().toISOString().slice(0, 10);
    doc.text(`Date: ${today}`, 40, 120);
    doc.text(`Billed To: ${orgName}`, 40, 140);
    doc.text(`Description: Usage charges (£0.10 per page)`, 40, 180);
    doc.text(`Total Pages: ${usage.totalPages}`, 40, 200);
    doc.text(`Amount Due: £${usage.amount.toFixed(2)}`, 40, 220);
    doc.line(40, 230, 555, 230);
    doc.text("Thank you for your business.", 40, 260);
    doc.save(`invoice-${orgName}-${today}.pdf`);
  };

  useEffect(() => {
    const loadDetail = async () => {
      if (!selectedInvoiceId) {
        setSelectedInvoice(null);
        return;
      }
      setLoading(true);
      try {
        const detail = await fetchInvoiceById(selectedInvoiceId);
        setSelectedInvoice(detail);
        setEmailTo(detail.billingEmail || "");
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    loadDetail();
  }, [selectedInvoiceId]);

  const handleDownload = async () => {
    if (!selectedInvoiceId) return;
    try {
      const blob = await downloadInvoicePdf(selectedInvoiceId);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${selectedInvoice?.invoiceNumber || selectedInvoiceId}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
    }
  };

  const handleEmail = async () => {
    if (!selectedInvoiceId || !emailTo) return;
    setLoading(true);
    setMessage("");
    try {
      await emailInvoice(selectedInvoiceId, emailTo);
      setMessage("Invoice email sent.");
    } catch (err) {
      setMessage("Failed to send invoice email.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <div style={{ background: "white", borderRadius: 10, padding: 16 }}>
        <div style={{ ...screenHeaderStyle }}>Usage & Charges</div>
        <div style={{ display: "flex", gap: 16, alignItems: "center", marginTop: 8 }}>
          <div style={{ fontSize: 14, color: "#333" }}>
            Organization: <b>{defaultOrganization?.name || "-"}</b>
          </div>
          <div style={{ fontSize: 14, color: "#333" }}>
            User: <b>{defaultUser ? `${defaultUser.firstName} ${defaultUser.lastName}` : "-"}</b>
          </div>
          {usage && (
            <div style={{ marginLeft: "auto", display: "flex", gap: 16, alignItems: "center" }}>
              <div style={{ fontSize: 14, color: "#333" }}>
                Total Pages: <b>{usage.totalPages.toLocaleString()}</b>
              </div>
              <div style={{ fontSize: 14, color: "#333" }}>
                Amount: <b>£{usage.amount.toFixed(2)}</b>
              </div>
              <button
                onClick={downloadUsageInvoicePdf}
                style={{ ...reviewScreenButtonWithOutColor, height: 36 }}
              >
                Download Invoice
              </button>
            </div>
          )}
        </div>
      </div>
      <div style={{ flex: 1, background: "white", borderRadius: 10, padding: 16 }}>
        <div style={{ ...screenHeaderStyle }}>Billing</div>
        {loading && <div>Loading...</div>}
        <div
          style={{
            marginTop: 8,
            display: "grid",
            gridTemplateColumns: "1fr auto auto",
            columnGap: 12,
            rowGap: 8,
          }}
        >
          {invoices.map((inv) => (
            <React.Fragment key={inv.id}>
              <div style={{ display: "flex", flexDirection: "column" }}>
                <span style={{ fontWeight: 600 }}>
                  {inv.invoiceNumber} • {inv.clientName}
                </span>
                <span style={{ fontSize: 12, color: "#666" }}>
                  {inv.issuedDate?.slice(0, 10)} → {inv.dueDate?.slice(0, 10)} • {inv.status}
                </span>
              </div>
              <div style={{ alignSelf: "center" }}>
                {inv.currency} {inv.amount.toLocaleString()}
              </div>
              <button
                onClick={() => setSelectedInvoiceId(inv.id)}
                style={{ ...reviewScreenButtonWithOutColor, height: 32 }}
              >
                View
              </button>
            </React.Fragment>
          ))}
        </div>
      </div>
      <div style={{ width: 400, background: "white", borderRadius: 10, padding: 16 }}>
        <div style={{ ...screenHeaderStyle }}>Invoice</div>
        {!selectedInvoice && (
          <div style={{ color: "#666", fontSize: 14 }}>Select an invoice to view details.</div>
        )}
        {selectedInvoice && (
          <div>
            <div style={{ marginBottom: 8 }}>
              <img src={logo as any} alt="Company Logo" style={{ height: 28 }} />
            </div>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <div>
                <div style={{ fontWeight: 700, fontSize: 18 }}>{selectedInvoice.invoiceNumber}</div>
                <div style={{ fontSize: 12, color: "#666" }}>{selectedInvoice.clientName}</div>
              </div>
              <div style={{ textAlign: "right" }}>
                <div style={{ fontWeight: 700 }}>
                  {selectedInvoice.currency} {selectedInvoice.amount.toLocaleString()}
                </div>
                <div style={{ fontSize: 12, color: "#666" }}>{selectedInvoice.status}</div>
              </div>
            </div>
            <div style={{ marginTop: 12, borderTop: "1px solid #eee", paddingTop: 12 }}>
              {selectedInvoice.items?.map((it, idx) => (
                <div
                  key={idx}
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr auto auto",
                    columnGap: 12,
                    marginBottom: 6,
                  }}
                >
                  <div>{it.description}</div>
                  <div style={{ textAlign: "right" }}>
                    {it.quantity} × {selectedInvoice.currency} {it.unitPrice.toLocaleString()}
                  </div>
                  <div style={{ textAlign: "right", fontWeight: 600 }}>
                    {selectedInvoice.currency} {it.total.toLocaleString()}
                  </div>
                </div>
              ))}
            </div>
            <div style={{ marginTop: 16, display: "flex", gap: 8 }}>
              <button
                onClick={handleDownload}
                style={{ ...reviewScreenButtonWithColor, height: 36 }}
              >
                Download PDF
              </button>
              <button
                onClick={handleEmail}
                style={{ ...reviewScreenButtonWithOutColor, height: 36 }}
              >
                Email
              </button>
            </div>
            <div style={{ marginTop: 12 }}>
              <input
                value={emailTo}
                onChange={(e) => setEmailTo(e.target.value)}
                placeholder="billing@company.com"
                style={{
                  width: "100%",
                  height: 32,
                  borderRadius: 8,
                  border: "1px solid #ddd",
                  padding: "0 10px",
                }}
              />
            </div>
            {message && <div style={{ marginTop: 8, fontSize: 12 }}>{message}</div>}
          </div>
        )}
      </div>
    </div>
  );
}
